import { ACHIEVEMENT_CATALOG, getAchievementById } from '../data/achievementCatalog';

const LEGACY_TITLE_TO_ID = {
  '🚀 10k Steps Champion': 'fit_10k_steps',
};

const defaultState = () => ({
  version: 1,
  counters: {
    tasksCompleted: 0,
    workoutsLogged: 0,
    maxLevel: 1,
  },
  unlockedIds: [],
  unlockedMeta: {},
  legacyUnlocked: [],
});

const uniqueStrings = (values = []) => Array.from(new Set(values.filter((value) => typeof value === 'string' && value.trim())));

export const migrateLegacyAchievements = (userData = {}) => {
  const next = { ...(userData || {}) };
  const prevAchievements = Array.isArray(next.achievements) ? next.achievements : [];
  const state = {
    ...defaultState(),
    ...(next.achievementState || {}),
    counters: {
      ...defaultState().counters,
      ...(next.achievementState?.counters || {}),
    },
    unlockedIds: Array.isArray(next.achievementState?.unlockedIds) ? [...next.achievementState.unlockedIds] : [],
    unlockedMeta: { ...(next.achievementState?.unlockedMeta || {}) },
    legacyUnlocked: Array.isArray(next.achievementState?.legacyUnlocked) ? [...next.achievementState.legacyUnlocked] : [],
  };

  // One-time migration from legacy string list to ID list.
  if ((next.achievementVersion || 0) < 1) {
    prevAchievements.forEach((label) => {
      const mappedId = LEGACY_TITLE_TO_ID[label];
      if (mappedId) {
        if (!state.unlockedIds.includes(mappedId)) {
          state.unlockedIds.push(mappedId);
          state.unlockedMeta[mappedId] = state.unlockedMeta[mappedId] || new Date().toISOString();
        }
      } else {
        state.legacyUnlocked.push(label);
      }
    });
  }

  // Ensure max level counter is not behind actual level.
  if (Number.isFinite(next.level)) {
    state.counters.maxLevel = Math.max(state.counters.maxLevel || 1, next.level);
  }

  next.achievementVersion = 1;
  next.achievementState = {
    ...state,
    version: 1,
    unlockedIds: uniqueStrings(state.unlockedIds),
    legacyUnlocked: uniqueStrings(state.legacyUnlocked),
  };
  next.achievements = buildCompatibilityAchievementTitles(next.achievementState);
  return next;
};

const buildCompatibilityAchievementTitles = (state) => {
  const catalogTitles = (state.unlockedIds || [])
    .map((id) => getAchievementById(id)?.title)
    .filter(Boolean);
  return uniqueStrings([...catalogTitles, ...(state.legacyUnlocked || [])]);
};

const getProgressValue = (category, counters) => {
  if (category === 'task') return counters.tasksCompleted || 0;
  if (category === 'workout') return counters.workoutsLogged || 0;
  if (category === 'level') return counters.maxLevel || 1;
  return 0;
};

const applyEventToCounters = (state, event = {}) => {
  const nextState = {
    ...state,
    counters: {
      ...state.counters,
    },
  };

  if (event.type === 'task_completed') {
    const count = Number.isFinite(event.count) ? event.count : 1;
    nextState.counters.tasksCompleted += Math.max(0, count);
  }

  if (event.type === 'workout_logged') {
    const count = Number.isFinite(event.count) ? event.count : 1;
    nextState.counters.workoutsLogged += Math.max(0, count);
  }

  if (Number.isFinite(event.level)) {
    nextState.counters.maxLevel = Math.max(nextState.counters.maxLevel, event.level);
  }

  return nextState;
};

export const applyAchievementEvent = (userData = {}, event = {}) => {
  const migrated = migrateLegacyAchievements(userData);
  let state = migrated.achievementState || defaultState();
  state = applyEventToCounters(state, event);

  const unlocked = [];
  const nextUnlockedIds = new Set(state.unlockedIds || []);
  const nextUnlockedMeta = { ...(state.unlockedMeta || {}) };

  ACHIEVEMENT_CATALOG.filter((entry) => entry.active).forEach((entry) => {
    const progress = getProgressValue(entry.category, state.counters);
    if (progress >= entry.target && !nextUnlockedIds.has(entry.id)) {
      nextUnlockedIds.add(entry.id);
      nextUnlockedMeta[entry.id] = new Date().toISOString();
      unlocked.push({ ...entry, progress, unlockedAt: nextUnlockedMeta[entry.id], unlocked: true });
    }
  });

  const nextState = {
    ...state,
    unlockedIds: Array.from(nextUnlockedIds),
    unlockedMeta: nextUnlockedMeta,
  };

  const nextUserData = {
    ...migrated,
    achievementVersion: 1,
    achievementState: nextState,
    achievements: buildCompatibilityAchievementTitles(nextState),
  };

  return {
    userData: nextUserData,
    unlocked,
    changed: unlocked.length > 0,
  };
};

export const getAchievementCards = (userData = {}) => {
  const migrated = migrateLegacyAchievements(userData);
  const state = migrated.achievementState || defaultState();
  const unlockedIds = new Set(state.unlockedIds || []);

  return ACHIEVEMENT_CATALOG.map((entry) => {
    const progress = getProgressValue(entry.category, state.counters);
    const unlocked = unlockedIds.has(entry.id);
    return {
      ...entry,
      progress,
      unlocked,
      unlockedAt: unlocked ? state.unlockedMeta?.[entry.id] || null : null,
    };
  }).sort((a, b) => {
    if (a.unlocked && b.unlocked) {
      return String(b.unlockedAt || '').localeCompare(String(a.unlockedAt || ''));
    }
    if (a.unlocked) return -1;
    if (b.unlocked) return 1;
    return b.progress - a.progress;
  });
};
